import React from 'react';
import { Subscription } from '../services/subscriptionService';
import { CreditCardIcon } from './Icons';

interface SubscriptionCardProps {
  subscription: Subscription | null;
  onUpgrade: () => void;
}

const LITE_PLAN_CODE = 'LP_LITE_INCLUDED';
const LITE_FEATURES = [
  'Up to 10 users',
  'Website forms & chatbot',
  'Leads, prospects & customers',
  'Follow-up reminders',
];

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscription, onUpgrade }) => {
  // Add null check for subscription
  if (!subscription) {
    return (
      <div className="bg-surface rounded-2xl shadow-lg border border-border p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-surface-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCardIcon size={6} className="text-fg-muted" />
          </div>
          <h3 className="text-lg font-semibold text-fg mb-2">No Subscription Found</h3>
          <p className="text-fg-secondary">Unable to load subscription information.</p>
        </div>
      </div>
    );
  }

  // LaunchPoint Lite is included with Mamba websites: no price, trial, or renewal.
  const isLite = subscription?.plan_code === LITE_PLAN_CODE || (subscription?.tier as string | undefined) === 'lite';

  const getPlanDisplayName = () => {
    const planCode = subscription?.plan_code;
    if (isLite) return 'LaunchPoint Lite';
    if (planCode) {
      const match = planCode.match(/^GS_(\w+)_|^LP_(\w+)_/);
      const tier = match ? (match[1] || match[2] || '').toLowerCase() : '';
      const names: Record<string, string> = {
        starter: 'Starter',
        growth: 'Growth',
        pro: 'Pro',
        elite: 'Elite',
      };
      if (planCode.startsWith('GS_')) {
        return names[tier] ? `Growth Stack ${names[tier]}` : planCode.replace(/_/g, ' ');
      }
      return names[tier] || planCode.replace(/_/g, ' ');
    }
    const legacy: Record<string, string> = {
      basic: 'Basic', pro: 'Pro', growth: 'Growth',
      starter: 'Starter', professional: 'Pro', enterprise: 'Growth',
    };
    return legacy[subscription?.plan || subscription?.tier || 'basic'] || 'Basic';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'trial':
        return 'text-info-fg bg-info-bg border-border';
      case 'active':
        return 'text-success-fg bg-success-bg border-border';
      case 'cancelled':
        return 'text-danger-fg bg-danger-bg border-danger-border';
      case 'past_due':
        return 'text-warning-fg bg-warning-bg border-border';
      default:
        return 'text-fg-secondary bg-surface-muted border-border';
    }
  };

  const getTrialDaysLeft = () => {
    if (!subscription?.status || subscription.status !== 'trial' || !subscription.trial_ends_at) {
      return null;
    }
    
    const trialEnd = new Date(subscription.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const trialDaysLeft = getTrialDaysLeft();
  
  // Normalize plan name to new tier system
  const normalizedPlan = (() => {
    const plan = subscription?.plan || subscription?.tier || 'basic';
    const mapping: Record<string, string> = {
      'starter': 'basic',
      'professional': 'pro',
      'enterprise': 'growth'
    };
    return mapping[plan] || plan;
  })();

  return (
    <div className="bg-surface rounded-2xl shadow-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-fg">Current Plan</h3>
          <p className="text-fg-secondary">Manage your subscription and billing</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(subscription?.status || 'unknown')}`}>
          {(subscription?.status || 'unknown').charAt(0).toUpperCase() + (subscription?.status || 'unknown').slice(1)}
        </div>
      </div>

      <div className="space-y-4">
        {/* Plan Details */}
        <div className="flex items-center justify-between p-4 bg-surface-muted rounded-xl">
          <div>
            <h4 className="text-lg font-semibold text-fg">
              {getPlanDisplayName()}
            </h4>
            {subscription?.includes_portalr && (
              <span className="text-xs text-primary font-medium">+ Portalr</span>
            )}
            <p className="text-fg-secondary">
              {isLite ? 'Included with your Mamba website' : (() => {
                const price = subscription?.price;
                const numPrice = typeof price === 'string' ? parseFloat(price) : (price || 0);
                const period = subscription?.billing_period || 'monthly';
                return numPrice === 0 ? 'Free' : `$${numPrice}/${period === 'yearly' ? 'yr' : 'mo'}`;
              })()}
            </p>
          </div>
          <div className="text-right">
            {subscription?.status === 'trial' && trialDaysLeft !== null && (
              <div className="text-sm text-info font-medium">
                {trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'Trial expired'}
              </div>
            )}
            {subscription?.status === 'active' && subscription?.current_period_end && (
              <div className="text-sm text-fg-secondary">
                Renews {new Date(subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Trial Banner */}
        {subscription?.status === 'trial' && trialDaysLeft !== null && trialDaysLeft > 0 && (
          <div className="p-4 alert-info rounded-xl">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-info mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-info-fg font-medium">
                  {trialDaysLeft <= 3 
                    ? `Your trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}`
                    : `You have ${trialDaysLeft} days left in your free trial`
                  }
                </p>
                <p className="text-info text-sm">
                  Upgrade now to continue using all features without interruption.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onUpgrade}
            className="flex-1 bg-gradient-to-r from-primary to-primary-dark text-primary-fg px-4 py-2 rounded-xl font-medium hover:from-primary-dark hover:to-primary transition-all duration-200"
          >
            {isLite ? 'Upgrade LaunchPoint' : subscription?.status === 'trial' ? 'Upgrade Plan' : 'Change Plan'}
          </button>
          {subscription?.status === 'active' && (
            <button className="px-4 py-2 border border-border text-fg-secondary rounded-xl font-medium hover:bg-surface-muted transition-colors duration-200">
              Billing History
            </button>
          )}
        </div>

        {/* Plan Features */}
        <div className="pt-4 border-t border-border">
          <h5 className="text-sm font-medium text-fg mb-2">Plan Features</h5>
          <div className="grid grid-cols-2 gap-2 text-sm text-fg-secondary">
            {isLite && LITE_FEATURES.map((feature) => (
              <div key={feature} className="flex items-center">
                <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </div>
            ))}
            {normalizedPlan === 'basic' && (
              <>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Up to 3 users
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Unlimited leads
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Fixed pipeline
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Email support
                </div>
              </>
            )}
            {normalizedPlan === 'pro' && (
              <>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Up to 10 users
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  5,000 emails/month
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Custom pipeline
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Priority support
                </div>
              </>
            )}
            {normalizedPlan === 'growth' && (
              <>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Up to 25 users
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  25,000 emails/month
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Workflow automation
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Dedicated support
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
